import EventEmitter from "./eventEmitter";
import {
  Divider,
  Drawer,
  makeStyles,
  Theme,
} from "@material-ui/core";
import {
  CommentIcon,
  PlayCircleIcon,
  CertificateIcon,
  UsersIcon,
  SignalIcon,
  FileCodeIcon,
} from "@panwds/icons";
import { Link } from "@panwds/react-ui";
import { useEffect, useState } from "react";
import { useTranslate } from "./customHooks";

const useSidebarStyles = makeStyles((theme) => ({
  paper: {
    position: "relative",
  },
  root: {
    display: "flex",
    alignItems: "center",
    // padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-end",
  },
  paperAnchorTop: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "50%",
  }
}));

const useStylesToggle = makeStyles((theme: Theme) => ({
  drawerHide: {
    display: "none",
  },
  drawerShow: {
    display: "flex",
    position: "absolute",
    right: "0",
    height: "100%",
    flexGrow: "initial",
    flexBasis: "auto",
  },
}));

const useCustomStyle = makeStyles((theme: Theme) => ({
  singleLineLink: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "24px",
  },
  singleLineLinkLastChild: {
    display: "flex",
    justifyContent: "space-between",
  },
  iconText: {
    display: "flex",
    alignItems: "center",
    "& svg": {
      marginLeft: "8px",
      marginRight: "10px",
    },
  },
  externalLinksContainer: {
    margin: "32px 32px 16px 8px",
  },
}));

const KnowledgeCenter = () => {
  const [toggle, setToggle] = useState(false);

  const classes = useSidebarStyles();
  const customStyleClass = useCustomStyle();
  const classesToggle = useStylesToggle();

  function toggleKnowledgeCenter() {
    setToggle(!toggle);
  }

  function closeKnowledgeCenter() {
    setToggle(false);
  }

  function openKnowledgeCenter() {
    setToggle(true);
  }

  useEffect(() => {
    EventEmitter.on("toggleKnowledgeCenter", toggleKnowledgeCenter);
    EventEmitter.on("openKnowledgeCenter", openKnowledgeCenter);
    EventEmitter.on("closeKnowledgeCenter", closeKnowledgeCenter);
    EventEmitter.on("close", closeKnowledgeCenter);

    return () => {
      EventEmitter.off("toggleKnowledgeCenter", toggleKnowledgeCenter);
      EventEmitter.off("openKnowledgeCenter", openKnowledgeCenter);
      EventEmitter.off("closeKnowledgeCenter", closeKnowledgeCenter);
      EventEmitter.off("close", closeKnowledgeCenter);
    };
  }, [toggle]);

  const translate = useTranslate();

  return (
    <Drawer
      classes={classes}
      anchor="right"
      variant="permanent"
      open={toggle}
      onClose={closeKnowledgeCenter}
      className={toggle ? classesToggle.drawerShow : classesToggle.drawerHide}
    >
      <Divider />
      <div className={customStyleClass.externalLinksContainer}>
        <div className={customStyleClass.singleLineLink}>
          <div className={customStyleClass.iconText}>
            <CommentIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-create-a-case"
            />
            <Link
              target="_blank"
              href={import.meta.env.VITE_SUPPORT_GET_HELP_URL}
              external
            >
              {translate("resources.knowledgeCenter.createACase")}
            </Link>
          </div>
        </div>
        <div className={customStyleClass.singleLineLink}>
          <div className={customStyleClass.iconText}>
            <PlayCircleIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-cloudNgfwVideoPlaylist"
            />
            <Link
              target="_blank"
              href="https://www.youtube.com/playlist?list=PLD6FJ8WNiIqWmfcE18dj7ItUiDECreaEc"
              external
            >
              {translate("resources.knowledgeCenter.cloudNgfwVideoPlaylist")}
            </Link>
          </div>
        </div>
        <div className={customStyleClass.singleLineLink}>
          <div className={customStyleClass.iconText}>
            <CertificateIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-getTrainedInNgfw"
            />
            <Link
              target="_blank"
              href="https://live.paloaltonetworks.com/t5/blogs/new-digital-course-for-cloud-ngfw-for-aws-in-beacon/ba-p/480213"
              external
            >
              {translate("resources.knowledgeCenter.getTrainedInNgfw")}
            </Link>
          </div>
        </div>
        <div className={customStyleClass.singleLineLink}>
          <div className={customStyleClass.iconText}>
            <UsersIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-cloudNgfwLiveCommunity"
            />
            <Link
              target="_blank"
              href="https://live.paloaltonetworks.com/t5/cloud-ngfw-help-center/ct-p/Cloud_NGFW"
              external
            >
              {translate("resources.knowledgeCenter.cloudNgfwLiveCommunity")}
            </Link>
          </div>
        </div>
        <div className={customStyleClass.singleLineLink}>
          <div className={customStyleClass.iconText}>
            <SignalIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-viewStatusPage"
            />
            <Link target="_blank" href="https://status.paloaltonetworks.com" external>
              {translate("resources.knowledgeCenter.viewStatusPage")}
            </Link>
          </div>
        </div>
        <div className={customStyleClass.singleLineLinkLastChild}>
          <div className={customStyleClass.iconText}>
            <FileCodeIcon
              className="tw-text-blue-600 dark:tw-text-dark-bg-blue"
              size="sm"
              data-metrics="cngfw-icons-apiDocumentation"
            />
            <Link target="_blank" href="https://pan.dev/cloudngfw/aws/api" external>
              {translate("resources.knowledgeCenter.apiDocumentation")}
            </Link>
          </div>
        </div>
      </div>
    </Drawer>
  );
};

export default KnowledgeCenter;
